import React from "react";

/* ================================================================
   World & Culture — 8 animated templates
   Globe, world map, cultural symbols, instruments, dance, festivals,
   languages, food
   Mix of flat 2D and soft 3D styles
   ================================================================ */

// ── 1. Globe With Pins ───────────────────────────────────────────────
export const GlobeWithPins: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const r = 140;
  const cx = 300;
  const cy = 220;
  const rot = rotation * 0.3;
  return (
    <svg viewBox="0 0 600 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <defs>
        <clipPath id="globeClip"><circle cx={cx} cy={cy} r={r} /></clipPath>
      </defs>
      {/* Ocean */}
      <circle cx={cx} cy={cy} r={r} fill="#2563eb" />
      {/* Continents (simple shapes rotating) */}
      <g clipPath="url(#globeClip)">
        {/* These shift with rotation to simulate spinning */}
        <ellipse cx={cx - 40 + rot % 300} cy={cy - 30} rx="45" ry="50" fill="#22c55e" opacity="0.7" />
        <ellipse cx={cx + 80 + rot % 300} cy={cy + 20} rx="35" ry="25" fill="#22c55e" opacity="0.7" />
        <ellipse cx={cx - 100 + rot % 300} cy={cy + 40} rx="50" ry="30" fill="#22c55e" opacity="0.6" />
        <ellipse cx={cx + 200 + rot % 300} cy={cy - 20} rx="30" ry="40" fill="#22c55e" opacity="0.5" />
        {/* Duplicate set for wrap-around */}
        <ellipse cx={cx - 40 + rot % 300 - 300} cy={cy - 30} rx="45" ry="50" fill="#22c55e" opacity="0.7" />
        <ellipse cx={cx + 80 + rot % 300 - 300} cy={cy + 20} rx="35" ry="25" fill="#22c55e" opacity="0.7" />
      </g>
      {/* Globe border */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1e3a8a" strokeWidth="2" />
      {/* Lat/long lines */}
      <ellipse cx={cx} cy={cy} rx={r} ry={r * 0.4} fill="none" stroke="#1e3a8a" strokeWidth="0.5" opacity="0.3" />
      <ellipse cx={cx} cy={cy} rx={r * 0.5} ry={r} fill="none" stroke="#1e3a8a" strokeWidth="0.5" opacity="0.3" />
      <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke="#1e3a8a" strokeWidth="0.5" opacity="0.3" />
      {/* Pins */}
      {[{ x: cx - 30, y: cy - 50, c: "#ef4444" }, { x: cx + 60, y: cy + 10, c: "#f59e0b" }, { x: cx - 80, y: cy + 30, c: "#6366f1" }].map((pin, i) => (
        <g key={i}>
          <line x1={pin.x} y1={pin.y} x2={pin.x} y2={pin.y - 20} stroke={pin.c} strokeWidth="2" />
          <circle cx={pin.x} cy={pin.y - 25} r="6" fill={pin.c}>
            <animate attributeName="r" values="5;7;5" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
      {/* Stand */}
      <ellipse cx={cx} cy={cy + r + 15} rx="60" ry="8" fill="#475569" />
      <rect x={cx - 4} y={cy + r} width="8" height="20" fill="#6b7280" />
      <text x={cx} y={cy + r + 55} textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">World Globe</text>
    </svg>
  );
};

// ── 2. World Map Animated ────────────────────────────────────────────
export const WorldMapAnimated: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const regions = [
    { name: "North America", x: 130, y: 140, w: 120, h: 80, color: "#3b82f6" },
    { name: "South America", x: 180, y: 250, w: 70, h: 100, color: "#22c55e" },
    { name: "Europe", x: 340, y: 110, w: 60, h: 60, color: "#8b5cf6" },
    { name: "Africa", x: 340, y: 200, w: 80, h: 120, color: "#f59e0b" },
    { name: "Asia", x: 430, y: 110, w: 150, h: 110, color: "#ef4444" },
    { name: "Oceania", x: 520, y: 280, w: 70, h: 50, color: "#ec4899" },
  ];
  return (
    <svg viewBox="0 0 700 420" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Ocean */}
      <rect x="50" y="50" width="600" height="320" rx="12" fill="#1e3a8a" />
      {/* Grid */}
      {[150, 200, 250, 300].map((y) => (
        <line key={y} x1="50" y1={y} x2="650" y2={y} stroke="#1e293b" strokeWidth="0.5" opacity="0.3" />
      ))}
      {/* Regions */}
      {regions.map((r, i) => (
        <g key={i}>
          <rect x={r.x} y={r.y} width={r.w} height={r.h} rx="6" fill={r.color} opacity={i === activeIndex ? 0.9 : 0.3}
                stroke={i === activeIndex ? "#fff" : "none"} strokeWidth={i === activeIndex ? 2 : 0}>
            {i === activeIndex && <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />}
          </rect>
          <text x={r.x + r.w / 2} y={r.y + r.h / 2 + 4} textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">{r.name}</text>
        </g>
      ))}
      {/* Connection lines */}
      <line x1={regions[0].x + 60} y1={regions[0].y + 40} x2={regions[2].x + 30} y2={regions[2].y + 30} stroke="#475569" strokeWidth="1" strokeDasharray="3 3" />
      <line x1={regions[2].x + 30} y1={regions[2].y + 30} x2={regions[4].x + 75} y2={regions[4].y + 55} stroke="#475569" strokeWidth="1" strokeDasharray="3 3" />
      <text x="350" y="400" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">{regions[activeIndex].name}</text>
    </svg>
  );
};

// ── 3. Cultural Symbols ──────────────────────────────────────────────
export const CulturalSymbols: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const symbols = [
    { label: "Peace", symbol: "☮", color: "#22c55e" },
    { label: "Harmony", symbol: "☯", color: "#6366f1" },
    { label: "Star", symbol: "✡", color: "#3b82f6" },
    { label: "Crescent", symbol: "☪", color: "#f59e0b" },
    { label: "Om", symbol: "🕉", color: "#ef4444" },
    { label: "Cross", symbol: "✝", color: "#8b5cf6" },
  ];
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="350" y="40" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Cultural Symbols</text>
      {symbols.map((s, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const sx = 140 + col * 200;
        const sy = 120 + row * 160;
        const isActive = i === activeIndex;
        return (
          <g key={i}>
            <circle cx={sx} cy={sy} r={isActive ? 55 : 50} fill="#0f172a" stroke={isActive ? s.color : "#334155"} strokeWidth={isActive ? 3 : 1.5}>
              {isActive && <animate attributeName="r" values="50;58;50" dur="1.5s" repeatCount="indefinite" />}
            </circle>
            <text x={sx} y={sy + 12} textAnchor="middle" fontSize="36">{s.symbol}</text>
            <text x={sx} y={sy + 45} textAnchor="middle" fill={isActive ? s.color : "#94a3b8"} fontSize="12" fontWeight="bold">{s.label}</text>
          </g>
        );
      })}
    </svg>
  );
};

// ── 4. Music Instruments ─────────────────────────────────────────────
export const MusicInstruments: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const instruments = ["Guitar", "Piano", "Drums", "Violin", "Flute"];
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Guitar */}
      <g opacity={activeIndex === 0 ? 1 : 0.3}>
        <ellipse cx="120" cy="240" rx="35" ry="45" fill="#92400e" stroke="#78350f" strokeWidth="2" />
        <ellipse cx="120" cy="180" rx="25" ry="30" fill="#a16207" stroke="#78350f" strokeWidth="2" />
        <rect x="115" y="100" width="10" height="80" rx="2" fill="#78350f" />
        <circle cx="120" cy="240" r="8" fill="#1e293b" />
        {[0, 1, 2, 3].map((s) => (
          <line key={s} x1={112 + s * 5} y1="140" x2={112 + s * 5} y2="270" stroke="#fbbf24" strokeWidth="0.5" />
        ))}
        {activeIndex === 0 && (
          <g>
            {[0, 1, 2].map((i) => (
              <path key={i} d={`M70,${220 + i * 10} Q50,${220 + i * 10} 40,${210 + i * 10}`} fill="none" stroke="#6366f1" strokeWidth="1.5" opacity="0.5">
                <animate attributeName="opacity" values="0;0.5;0" dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
              </path>
            ))}
          </g>
        )}
        <text x="120" y="310" textAnchor="middle" fill="#e0e7ff" fontSize="11">Guitar</text>
      </g>
      {/* Piano */}
      <g opacity={activeIndex === 1 ? 1 : 0.3}>
        <rect x="220" y="170" width="140" height="100" rx="4" fill="#1e293b" stroke="#475569" strokeWidth="2" />
        {Array.from({ length: 8 }).map((_, i) => (
          <rect key={i} x={225 + i * 16.5} y="180" width="14" height="70" rx="1" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="0.5" />
        ))}
        {[0, 1, 3, 4, 5].map((i) => (
          <rect key={`b-${i}`} x={234 + i * 16.5} y="180" width="10" height="42" rx="1" fill="#1e293b" />
        ))}
        {activeIndex === 1 && (
          <g>
            {["♪", "♫", "♪"].map((n, i) => (
              <text key={i} x={250 + i * 30} y={160 - i * 10} fill="#6366f1" fontSize="14" opacity="0.6">
                {n}
                <animate attributeName="y" values={`${160 - i * 10};${140 - i * 10};${160 - i * 10}`} dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0;0.6;0" dur="2s" repeatCount="indefinite" />
              </text>
            ))}
          </g>
        )}
        <text x="290" y="310" textAnchor="middle" fill="#e0e7ff" fontSize="11">Piano</text>
      </g>
      {/* Drums */}
      <g opacity={activeIndex === 2 ? 1 : 0.3}>
        <ellipse cx="450" cy="220" rx="40" ry="15" fill="#78350f" stroke="#5b2c0f" strokeWidth="2" />
        <rect x="410" y="220" width="80" height="50" fill="#a16207" stroke="#78350f" strokeWidth="2" />
        <ellipse cx="450" cy="270" rx="40" ry="15" fill="#92400e" />
        {activeIndex === 2 && (
          <g>
            <line x1="430" y1="180" x2="430" y2="220" stroke="#6b7280" strokeWidth="2" />
            <circle cx="430" cy="178" r="4" fill="#6b7280">
              <animate attributeName="cy" values="178;175;178" dur="0.3s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
        <text x="450" y="310" textAnchor="middle" fill="#e0e7ff" fontSize="11">Drums</text>
      </g>
      {/* Violin */}
      <g opacity={activeIndex === 3 ? 1 : 0.3}>
        <ellipse cx="580" cy="250" rx="20" ry="30" fill="#a16207" />
        <ellipse cx="580" cy="195" rx="15" ry="22" fill="#92400e" />
        <rect x="576" y="140" width="8" height="55" fill="#78350f" />
        <line x1="575" y1="155" x2="550" y2="280" stroke="#fbbf24" strokeWidth="1" opacity="0.5" />
        <text x="580" y="310" textAnchor="middle" fill="#e0e7ff" fontSize="11">Violin</text>
      </g>
      {/* Flute */}
      <g opacity={activeIndex === 4 ? 1 : 0.3}>
        <rect x="660" y="210" width="100" height="12" rx="6" fill="#c0c0c0" stroke="#94a3b8" strokeWidth="1" />
        {[0, 1, 2, 3, 4].map((h) => (
          <circle key={h} cx={670 + h * 18} cy="216" r="3" fill="#6b7280" />
        ))}
        <text x="710" y="310" textAnchor="middle" fill="#e0e7ff" fontSize="11">Flute</text>
      </g>
      <text x="400" y="370" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">{instruments[activeIndex]}</text>
    </svg>
  );
};

// ── 5. Traditional Dance ─────────────────────────────────────────────
export const TraditionalDance: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const sway = Math.sin(phase * 1.5) * 8;
  const armAngle = Math.sin(phase * 2) * 20;
  return (
    <svg viewBox="0 0 600 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Stage */}
      <rect x="50" y="350" width="500" height="30" rx="5" fill="#854d0e" />
      <rect x="30" y="375" width="540" height="15" rx="3" fill="#78350f" />
      {/* Spotlight */}
      <polygon points="300,0 200,350 400,350" fill="#fbbf24" opacity="0.05" />
      {/* Dancer */}
      <g transform={`translate(${sway},0)`}>
        {/* Head */}
        <circle cx="300" cy="160" r="20" fill="#fbbf24" />
        {/* Crown / headpiece */}
        <polygon points="285,145 300,125 315,145" fill="#f59e0b" />
        <circle cx="300" cy="130" r="4" fill="#ef4444" />
        {/* Body */}
        <rect x="288" y="180" width="24" height="40" rx="5" fill="#ec4899" />
        {/* Skirt */}
        <polygon points={`270,220 ${300 + sway * 0.5},340 ${330 - sway * 0.5},220`} fill="#d946ef" />
        <polygon points={`280,220 ${300 + sway * 0.3},330 ${320 - sway * 0.3},220`} fill="#a855f7" opacity="0.5" />
        {/* Arms */}
        <line x1="288" y1="190" x2={260 + armAngle} y2={220 - Math.abs(armAngle)} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        <line x1="312" y1="190" x2={340 - armAngle} y2={220 - Math.abs(armAngle)} stroke="#fbbf24" strokeWidth="5" strokeLinecap="round" />
        {/* Hands */}
        <circle cx={260 + armAngle} cy={220 - Math.abs(armAngle)} r="4" fill="#fbbf24" />
        <circle cx={340 - armAngle} cy={220 - Math.abs(armAngle)} r="4" fill="#fbbf24" />
        {/* Feet */}
        <ellipse cx={285 + sway * 0.3} cy="345" rx="8" ry="4" fill="#fbbf24" />
        <ellipse cx={315 - sway * 0.3} cy="345" rx="8" ry="4" fill="#fbbf24" />
      </g>
      {/* Music notes */}
      {["♪", "♫", "♪"].map((n, i) => (
        <text key={i} x={150 + i * 150} y={100 + i * 20} fill="#ec4899" fontSize="18" opacity="0.4">
          {n}
          <animate attributeName="y" values={`${100 + i * 20};${80 + i * 20};${100 + i * 20}`} dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
        </text>
      ))}
      <text x="300" y="420" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Traditional Dance</text>
    </svg>
  );
};

// ── 6. Festival Lanterns ─────────────────────────────────────────────
export const FestivalLanterns: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const colors = ["#ef4444", "#fbbf24", "#f97316", "#ec4899", "#22c55e", "#6366f1", "#3b82f6"];
  return (
    <svg viewBox="0 0 800 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Night sky */}
      <rect x="0" y="0" width="800" height="450" fill="#0f172a" rx="12" />
      {/* Stars */}
      {Array.from({ length: 20 }).map((_, i) => (
        <circle key={i} cx={40 + (i * 97) % 720} cy={20 + (i * 43) % 200} r="1" fill="#fff" opacity={0.3 + Math.sin(phase * 0.2 + i) * 0.2} />
      ))}
      {/* Lanterns floating */}
      {Array.from({ length: 7 }).map((_, i) => {
        const lx = 80 + i * 95;
        const ly = 150 + Math.sin(phase * 0.3 + i * 0.7) * 25;
        const color = colors[i];
        return (
          <g key={i}>
            {/* String */}
            <line x1={lx + 20} y1={20} x2={lx + 20} y2={ly} stroke="#94a3b8" strokeWidth="1" opacity="0.3" />
            {/* Lantern body */}
            <ellipse cx={lx + 20} cy={ly + 25} rx="22" ry="30" fill={color} opacity="0.8" />
            <ellipse cx={lx + 20} cy={ly + 25} rx="15" ry="22" fill={color} opacity="0.4" />
            {/* Top cap */}
            <rect x={lx + 10} y={ly} width="20" height="8" rx="3" fill="#475569" />
            {/* Bottom cap */}
            <rect x={lx + 12} y={ly + 50} width="16" height="5" rx="2" fill="#475569" />
            {/* Glow */}
            <circle cx={lx + 20} cy={ly + 25} r="28" fill={color} opacity="0.1">
              <animate attributeName="r" values="25;32;25" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            </circle>
            {/* Tassel */}
            <line x1={lx + 20} y1={ly + 55} x2={lx + 18} y2={ly + 70} stroke={color} strokeWidth="1.5">
              <animate attributeName="x2" values={`${lx + 16};${lx + 22};${lx + 16}`} dur="2s" repeatCount="indefinite" />
            </line>
          </g>
        );
      })}
      <text x="400" y="420" textAnchor="middle" fill="#fbbf24" fontSize="16" fontWeight="bold">Festival Lanterns</text>
    </svg>
  );
};

// ── 7. Language Bubbles ──────────────────────────────────────────────
export const LanguageBubbles: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const langs = [
    { hello: "Hello", lang: "English", color: "#3b82f6" },
    { hello: "Hola", lang: "Spanish", color: "#ef4444" },
    { hello: "Bonjour", lang: "French", color: "#8b5cf6" },
    { hello: "こんにちは", lang: "Japanese", color: "#ec4899" },
    { hello: "مرحبا", lang: "Arabic", color: "#22c55e" },
    { hello: "Привет", lang: "Russian", color: "#f59e0b" },
  ];
  return (
    <svg viewBox="0 0 700 420" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="350" y="35" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Hello Around the World</text>
      {langs.map((l, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const bx = 120 + col * 200;
        const by = 110 + row * 170;
        const isActive = i === activeIndex;
        return (
          <g key={i}>
            {/* Bubble */}
            <ellipse cx={bx} cy={by} rx={isActive ? 80 : 70} ry={isActive ? 45 : 38} fill={l.color} opacity={isActive ? 0.9 : 0.25}
                     stroke={isActive ? "#fff" : "none"} strokeWidth={isActive ? 2 : 0}>
              {isActive && <animate attributeName="rx" values="75;85;75" dur="1.5s" repeatCount="indefinite" />}
            </ellipse>
            {/* Tail */}
            <polygon points={`${bx - 10},${by + 35} ${bx + 10},${by + 35} ${bx},${by + 55}`} fill={l.color} opacity={isActive ? 0.9 : 0.25} />
            {/* Text */}
            <text x={bx} y={by + 5} textAnchor="middle" fill="#fff" fontSize={isActive ? 18 : 14} fontWeight="bold">{l.hello}</text>
            <text x={bx} y={by + 65} textAnchor="middle" fill={isActive ? l.color : "#475569"} fontSize="11">{l.lang}</text>
          </g>
        );
      })}
    </svg>
  );
};

// ── 8. Food Around the World ─────────────────────────────────────────
export const FoodWorld: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const foods = [
    { name: "Pizza", emoji: "🍕", origin: "Italy", color: "#ef4444" },
    { name: "Sushi", emoji: "🍣", origin: "Japan", color: "#ec4899" },
    { name: "Taco", emoji: "🌮", origin: "Mexico", color: "#f59e0b" },
    { name: "Croissant", emoji: "🥐", origin: "France", color: "#92400e" },
    { name: "Curry", emoji: "🍛", origin: "India", color: "#f97316" },
    { name: "Noodles", emoji: "🍜", origin: "China", color: "#22c55e" },
  ];
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <text x="350" y="35" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Flavors of the World</text>
      {foods.map((f, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const fx = 130 + col * 200;
        const fy = 110 + row * 150;
        const isActive = i === activeIndex;
        return (
          <g key={i}>
            {/* Plate */}
            <ellipse cx={fx} cy={fy + 20} rx={isActive ? 55 : 48} ry={isActive ? 25 : 22} fill="#1e293b" stroke={isActive ? f.color : "#334155"} strokeWidth={isActive ? 2 : 1}>
              {isActive && <animate attributeName="rx" values="50;58;50" dur="1.5s" repeatCount="indefinite" />}
            </ellipse>
            {/* Food emoji */}
            <text x={fx} y={fy + 10} textAnchor="middle" fontSize={isActive ? 36 : 28}>{f.emoji}</text>
            {/* Label */}
            <text x={fx} y={fy + 55} textAnchor="middle" fill={isActive ? "#fff" : "#94a3b8"} fontSize="12" fontWeight="bold">{f.name}</text>
            <text x={fx} y={fy + 70} textAnchor="middle" fill={isActive ? f.color : "#475569"} fontSize="9">{f.origin}</text>
          </g>
        );
      })}
    </svg>
  );
};
